import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { placementAPI } from '../../api/axios';
import { toast } from 'react-toastify';
import AdminNav from '../../components/AdminNav';
import { Users, Building2, Briefcase, IndianRupee, Loader2, Clock } from 'lucide-react';

export default function ActivePlacements() {
  const navigate = useNavigate();
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    placementAPI.activePlacements()
      .then(res => setPlacements(res.data.data || []))
      .catch(() => toast.error('Failed to load placements'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="h-10 w-10 animate-spin text-teal-600" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNav
        title="Active Placements"
        subtitle={`${placements.length} staff currently hired`}
      />
      <div className="max-w-7xl mx-auto px-6 py-8">
        {placements.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-2xl border border-gray-100 shadow-sm">
            <Users className="h-14 w-14 text-gray-200 mx-auto mb-4" />
            <p className="text-gray-400 font-semibold">No active placements</p>
          </div>
        ) : (
          <div className="space-y-4">
            {placements.map(p => (
              <div key={p.id}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600
                      rounded-xl flex items-center justify-center text-white font-black shrink-0">
                      {p.staffFirstName?.charAt(0)}{p.staffLastName?.charAt(0)}
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-800">
                        {p.staffFirstName} {p.staffLastName}
                      </h3>
                      {p.staffTypeName && (
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <Briefcase className="h-3 w-3" /> {p.staffTypeName}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-blue-500" />
                    <div>
                      <p className="font-semibold text-slate-700 text-sm">{p.companyName}</p>
                      <p className="text-xs text-gray-400">{p.organizationType}</p>
                    </div>
                  </div>

                  <div className="flex gap-4 text-sm text-gray-600">
                    {p.roleName && (
                      <div>
                        <p className="text-xs text-gray-400">Role</p>
                        <p className="font-semibold">{p.roleName}</p>
                      </div>
                    )}
                    {p.dailyRateAgreed && (
                      <div>
                        <p className="text-xs text-gray-400">Daily Rate</p>
                        <p className="font-semibold flex items-center gap-0.5">
                          <IndianRupee className="h-3 w-3" />₹{p.dailyRateAgreed}
                        </p>
                      </div>
                    )}
                    {p.shiftHours && (
                      <div>
                        <p className="text-xs text-gray-400">Shift</p>
                        <p className="font-semibold flex items-center gap-1">
                          <Clock className="h-3 w-3" />{p.shiftHours}hr
                        </p>
                      </div>
                    )}
                    {p.startDate && (
                      <div>
                        <p className="text-xs text-gray-400">Since</p>
                        <p className="font-semibold text-xs">
                          {new Date(p.startDate).toLocaleDateString('en-IN', {
                            day: 'numeric', month: 'short', year: 'numeric'
                          })}
                        </p>
                      </div>
                    )}
                  </div>

                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">
                    {p.source === 'OPPORTUNITY' ? 'Via Opportunity' : 'Direct Hire'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
